/**
 * steps/PropFirmStep.tsx
 * Prop firm setup step for the onboarding wizard
 * 
 * Handles prop firm selection and account stage configuration with complex business logic:
 * - Asset-based firm filtering and availability checking
 * - Invalid selection clearing when assets change
 * - Custom firm name support with conditional validation
 * - Dynamic account stage options based on selected firm
 * - Cascade clearing of broker selection when firm changes
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo } from 'react';
import { Building, Users } from 'lucide-react';

// 2. Internal absolute imports
import { Select, SelectItem, RadioGroup, Radio, Input, Card, CardBody, CardHeader, Alert } from '@enfomentis/ui';
import type { PropAccountStage } from '@/types/reference/PropFirmCatalog';
import { getPropFirmsByAssetClasses, getAccountTypesForPropFirm, PROP_ACCOUNT_STAGES } from '@/data/propFirmCatalog';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Prop firm setup step component
 */
export const PropFirmStep: React.FC = () => {
  const { draft, updateAccount, clearBrokerSelection } = useOnboardingData();
  
  // Local form state matching PropFirmScreen pattern
  const [formData, setFormData] = useState({
    firmName: '',
    accountStage: '' as PropAccountStage | '',
    customFirmName: ''
  });

  // Get selected asset classes from previous screen
  const selectedAssets = useMemo(() => 
    draft?.account?.selectedAssetClasses || [], 
    [draft?.account?.selectedAssetClasses]
  );

  // Get available prop firms based on selected assets
  const availablePropFirms = useMemo(() => 
    selectedAssets.length > 0 ? getPropFirmsByAssetClasses(selectedAssets) : [],
    [selectedAssets]
  );

  // Sort firms alphabetically by name
  const sortedPropFirms = useMemo(() => 
    [...availablePropFirms].sort((a, b) => a.name.localeCompare(b.name)),
    [availablePropFirms]
  );

  // Convert prop firms to Select options
  const propFirmOptions = useMemo(() => {
    const firmOptions = sortedPropFirms.map(firm => ({
      key: firm.id,
      label: firm.name
    }));
    
    // Only add "other-custom" if it's not already in the list
    const hasOtherCustom = sortedPropFirms.some(firm => firm.id === 'other-custom');
    if (!hasOtherCustom) {
      firmOptions.push({ key: 'other-custom', label: 'Other (Custom)' });
    }
    
    return firmOptions;
  }, [sortedPropFirms]);

  // Get available account stages for selected firm
  const availableAccountTypes = useMemo(() => 
    formData.firmName ? getAccountTypesForPropFirm(formData.firmName) : PROP_ACCOUNT_STAGES,
    [formData.firmName]
  );

  // Initialize from draft when loaded (with validation for asset compatibility)
  useEffect(() => {
    if (draft?.account?.propFirmDetails && !formData.firmName && !formData.accountStage) {
      const savedFirmName = draft.account.propFirmDetails.firmName;
      
      // Check if saved prop firm selection is still valid for current assets
      const isSelectionValid = savedFirmName && 
        availablePropFirms.some(firm => firm.id === savedFirmName);
      
      if (isSelectionValid || savedFirmName === 'other-custom') {
        // Keep valid selection
        setFormData({
          firmName: draft.account.propFirmDetails.firmName || '',
          accountStage: draft.account.propFirmDetails.accountStage || '',
          customFirmName: draft.account.propFirmDetails.customFirmName || ''
        });
      } else if (savedFirmName) {
        // Clear invalid selection and cascade clear broker selection
        setFormData({
          firmName: '',
          accountStage: '',
          customFirmName: ''
        });
        clearBrokerSelection();
      }
    }
  }, [draft?.account?.propFirmDetails, formData.firmName, formData.accountStage, availablePropFirms, clearBrokerSelection]);

  // Update draft when form changes
  useEffect(() => {
    if (formData.firmName || formData.accountStage) {
      updateAccount({
        propFirmDetails: {
          firmName: formData.firmName,
          accountStage: formData.accountStage as PropAccountStage,
          customFirmName: formData.customFirmName
        }
      });
    }
  }, [formData, updateAccount]);

  /**
   * Handle prop firm selection changes
   */
  const handleFirmChange = (value: string): void => {
    setFormData(prev => ({
      ...prev,
      firmName: value,
      accountStage: '', // Reset stage when firm changes
      customFirmName: value === 'other-custom' ? prev.customFirmName : ''
    }));
  };

  /**
   * Handle account stage selection changes
   */
  const handleStageChange = (value: string): void => {
    setFormData(prev => ({
      ...prev,
      accountStage: value as PropAccountStage
    }));
  };

  /**
   * Handle custom firm name changes
   */
  const handleCustomNameChange = (value: string): void => {
    setFormData(prev => ({
      ...prev,
      customFirmName: value
    }));
  };

  /**
   * Check if form is valid for navigation
   */
  const isFormValid = (): boolean => {
    if (!formData.firmName || !formData.accountStage) {
      return false;
    }
    if (formData.firmName === 'other-custom' && !formData.customFirmName.trim()) {
      return false;
    }
    return true;
  };

  return (
    <div className="max-w-2xl mx-auto px-2 pb-2">
      {/* Prop Firm Setup Form */}
      <div className="space-y-6">
        <div>
          <h3 className="text-large font-semibold text-text-primary mb-2">Prop Firm Setup</h3>
          <p className="text-sm text-text-secondary mb-6">Configure your proprietary trading firm details to get personalized broker recommendations.</p>
        </div>

        {/* Prop Firm Selection */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
            <Building className="w-5 h-5 text-primary" />
            <span>Select Prop Firm</span>
          </div>
        <Select
          placeholder="Choose a prop firm..."
          selectedKeys={formData.firmName ? [formData.firmName] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            if (selected) handleFirmChange(selected);
          }}
          isDisabled={availablePropFirms.length === 0}
          isRequired
          description="Available prop firms are filtered based on your selected asset classes"
          size="lg"
          aria-label="Select prop firm"
        >
          {propFirmOptions.map((option) => (
            <SelectItem key={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
        </div>

        {/* No firms available warning */}
        {availablePropFirms.length === 0 && selectedAssets.length > 0 && (
          <Alert
            color="warning"
            variant="flat"
            description='No prop firms available for the selected asset classes. You may need to adjust your asset selection or choose "Other (Custom)".'
          />
        )}

        {/* Custom Firm Name Input */}
        {formData.firmName === 'other-custom' && (
          <Input
            label="Custom Prop Firm Name"
            placeholder="Enter prop firm name"
            value={formData.customFirmName}
            onValueChange={handleCustomNameChange}
            isRequired
            variant="bordered"
            size="lg"
            description="Enter the name of your prop firm if it's not listed above"
            errorMessage={
              formData.customFirmName.length > 0 && formData.customFirmName.trim().length === 0
                ? "Custom prop firm name is required"
                : undefined
            }
            isInvalid={formData.customFirmName.length > 0 && formData.customFirmName.trim().length === 0}
          />
        )}

        {/* Account Stage Selection */}
        {formData.firmName && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
              <Users className="w-5 h-5 text-primary" />
              <span>Account Stage</span>
            </div>
            <RadioGroup
              label=""
              value={formData.accountStage}
              onValueChange={handleStageChange}
              orientation="vertical"
              size="lg"
              isRequired
              description="Select your current account status with the prop firm"
              classNames={{
                wrapper: "space-y-3"
              }}
            >
              {availableAccountTypes.map((stage) => (
                <Card 
                  key={stage.id}
                  isPressable
                  isHoverable
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.accountStage === stage.id
                      ? 'border-primary bg-primary-50 shadow-md' 
                      : 'border-border-light hover:border-border-medium hover:shadow-sm'
                  }`}
                  onPress={() => handleStageChange(stage.id)}
                >
                  <CardBody className="p-4">
                    <div className="flex items-start gap-4">
                      <Radio
                        value={stage.id}
                        size="lg"
                        classNames={{
                          base: "m-0 p-0"
                        }}
                      />
                      <div className="flex-1">
                        <div className="mb-2">
                          <span className={`font-medium ${
                            formData.accountStage === stage.id ? 'text-primary' : 'text-text-primary'
                          }`}>
                            {stage.name}
                          </span>
                        </div>
                        <p className="text-sm text-text-secondary leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Validation Status */}
        {isFormValid() && (
          <div className="flex items-center gap-2 text-success text-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
            Prop firm setup complete! Ready to continue.
          </div>
        )}
      </div>

      {/* Additional Context */}
      <Card className="mt-8">
        <CardHeader className="pb-2">
          <h4 className="font-bold text-large">💡 Why do we need this?</h4>
        </CardHeader>
        <CardBody className="pt-0">
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Your prop firm selection helps us show compatible brokers and platforms</li>
            <li>• Account stage determines available features and trading limits</li>
            <li>• This enables personalized recommendations throughout your setup</li>
            <li>• Different firms have different requirements and fee structures</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
};